import { differenceInMinutes, addMinutes, isBefore, addDays, isAfter } from 'date-fns'

import { store } from 'store/reducers'
import { Parse, Format, setTime, OutputFormat } from 'services/Dates'
import { getStartEndFromTimeDate } from 'services/schedule'
import { getBreaksQueryCache } from 'Api'

export default function getBreaksToApply(event: { start: string; end: string; positionId?: string }, positionId?: string): MData.ShiftBreak[] {
  const [start, end] = getStartEndFromTimeDate({ ...event })

  return getBreaksFromTime({ start, end, positionId: event.positionId || positionId })
}

type GetBreaksFromTime = (args: { start: Date | string; end: Date | string; positionId?: string }) => MData.Break[]

export const getBreaksFromTime: GetBreaksFromTime = ({ start, end, positionId }) => {
  const {
    resources: { positionsById }
  } = store.getState() as MStore.RootState
  const data = getBreaksQueryCache()
  const breaks = data?.breaks || []
  const toApply: MData.Break[] = []

  const [parsedStart, parsedEnd] = [Parse(start), Parse(end)]

  const totalDuration = differenceInMinutes(parsedEnd, parsedStart)

  // Check all breaks to see which ones to apply based on
  // duration or a specific start time
  for (const breakObj of breaks) {
    const { name, duration, paid, autoAssign, threshold, scheduledTime, departments } = breakObj

    // Check if the break is assigned to one of the department the position is part of
    if (departments?.length && positionId && !departments?.some(department => positionsById[positionId]?.departments?.includes(department))) continue

    if (autoAssign === 'THRESHOLD' && threshold) {
      if (threshold <= totalDuration) toApply.push({ name: breakObj.name, duration: breakObj.duration, paid: breakObj.paid })
    } else if (breakObj.autoAssign === 'SCHEDULED_TIME' && scheduledTime) {
      // Determine if the break scheduled time is contained within the punch card
      let breakStart = setTime(parsedStart, scheduledTime)

      // Handles overnight punch cards
      if (isBefore(breakStart, parsedStart)) breakStart = addDays(breakStart, 1)

      // If the break is after the end, it is not contained in punch card
      if (isBefore(breakStart, parsedEnd)) {
        // Get the break end
        let breakEnd = addMinutes(breakStart, duration)

        // Handles edge case where break finishes after punch card end
        if (isAfter(breakEnd, parsedEnd)) breakEnd = parsedEnd

        // Add the break to the punch card
        toApply.push({ scheduledTime: Format(breakStart), name, duration: differenceInMinutes(breakEnd, breakStart), paid })
      }
    }
  }

  return toApply
}

export function getPunchCardBreaksToApply(punchCard: MData.PunchCard): Partial<MData.Break>[] {
  const breaksToApply = getBreaksFromTime({ start: punchCard.arrival, end: punchCard.leave })
  return formatPunchCardBreaks({ arrival: Parse(punchCard.arrival), leave: Parse(punchCard.leave), breaksToApply })
}

type FormatPunchCardBreaks = (args: { arrival: Date; leave: Date; breaksToApply: MData.Break[]; outFormat?: OutputFormat }) => MData.PunchBreak[]

export const formatPunchCardBreaks: FormatPunchCardBreaks = ({ arrival, leave, breaksToApply, outFormat }) => {
  const totalDuration = differenceInMinutes(leave, arrival)
  const formattedBreaksToApply = breaksToApply.map(breakObj => {
    const { name, duration, paid, scheduledTime: presetScheduledTime } = breakObj
    const scheduledTime = presetScheduledTime ? Parse(presetScheduledTime) : undefined

    // Prevents break from being set outside of punch card hours unless break is longer than punch duration.
    const breakOffset = !scheduledTime && duration ? Math.max(totalDuration - duration, 0) / 2 : 0

    const start = addMinutes(scheduledTime || arrival, breakOffset)
    const end = addMinutes(scheduledTime || arrival, breakOffset + (duration ?? 0))

    return { name, start: outFormat ? Format(start, { outFormat }) : start, end: outFormat ? Format(end, { outFormat }) : end, paid, duration }
  })

  return formattedBreaksToApply
}
